import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      navigate('/cart');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrderDetails(response.data);
          await clearCart();
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Failed to check payment status:', error);
        setStatus('error');
      }
    };

    poll();
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="checking-payment">
        <Card className="p-12 text-center border-0 shadow-xl max-w-md">
          <div className="spinner mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12" data-testid="payment-success-page">
        <Card className="p-12 text-center border-0 shadow-xl max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-gray-900" data-testid="success-title">
            Payment Successful!
          </h1>
          <p className="text-gray-600 mb-6">
            Thank you for your order. We've received your payment and will start processing your order shortly.
          </p>
          {orderDetails && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">Order ID</p>
              <p className="font-mono font-semibold text-lg" data-testid="order-id">{orderDetails.order_id}</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Link to="/dashboard">
              <Button className="w-full bg-[#FF6B35] hover:bg-[#ff8c5a] text-white rounded-full py-6" data-testid="view-orders-button">
                View My Orders
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="w-full rounded-full py-6 border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="payment-error">
      <Card className="p-12 text-center border-0 shadow-xl max-w-md">
        <h2 className="text-2xl font-bold mb-4 text-red-600">Payment Issue</h2>
        <p className="text-gray-600 mb-6">
          {status === 'expired' ? 'Your payment session has expired.' : 'There was an issue processing your payment.'}
        </p>
        <Link to="/cart">
          <Button className="bg-[#FF6B35] hover:bg-[#ff8c5a] text-white rounded-full px-8">
            Back to Cart
          </Button>
        </Link>
      </Card>
    </div>
  );
}
